import { Component, OnInit, Output, EventEmitter, Input, ViewChild, ElementRef } from '@angular/core';
import notify from 'devextreme/ui/notify';
import validationEngine from "devextreme/ui/validation_engine";
import { TaskDataService } from '../../../shared/services/task-data.service';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { LoggingService } from '../../../framework/logging/logging.service';
import { SeverityLevel } from '../../../framework/logging/severity-level.model';
import { IPayoffPayDownRequestData, ITaskRequest, PayoffPayDownRequestData, TaskRequest } from '../../../modules/tasks/model/types';
import { getNotificationTimeout, sendNotificationMessage } from '../../../types/resource';
import { MessageService } from '../../../shared/components/framework/services/message.service';
import { FormUtils } from '../../../shared/components/framework/services/form-builder.service';
import { LoanDataBase } from '../baseclass/loandata-base';
import { FileUploadMode } from 'devextreme/ui/file_uploader';

@Component({
  selector: 'app-payoff-paydown',
  templateUrl: './payoff-paydown.component.html',
  styleUrls: ['./payoff-paydown.component.css'],
})
export class PayoffPaydownComponent extends LoanDataBase implements OnInit {
  @ViewChild('formElement') formElement!: ElementRef;

  @Input() selectButtonText : string ="Browse or drop files here";
  @Input() labelText: string = "Browse or drop files here";
  @Input() accept: string = "*";
  @Input() fileUploadMode: FileUploadMode ="instantly";// "useButtons";
  @Input() multiple: boolean = true;
  @Input() isEditable: boolean = true;
  @Input() taskTemplateName: string;
  @Input() task: ITaskRequest;

  @Output() onSubmitTaskEventEmitter: EventEmitter<any> = new EventEmitter();
  @Output() onFileUploadEventEmitter: EventEmitter<File> = new EventEmitter();
  @Output() onFileRemoveEventEmitter: EventEmitter<File> = new EventEmitter();

  payoffPaydownData: IPayoffPayDownRequestData;
  requestTypes: any[];
  transactiontypes: any[];
  triggertypes: any[];
  isUrgent: boolean = false;
  isRushFee: boolean = false;
  expectedPayoffDate: Date = new Date();
  nextTaxDueDate: Date | null = null;
  isLoading: boolean;
  files: File[] = [];
  override showInactiveLoans: boolean = false;
  currentFormInstance: any = null;
  currentRequestType: string = "";
  reservesOnLoan: boolean = false;
  isGridInEditMode: boolean = false;
  shouldDisableCheckbox: boolean = false;
  isUpdatingLoanData: boolean = false;

  formColCountBehaviourSubject$ = new BehaviorSubject<number>(2);
  formColCount$ = this.formColCountBehaviourSubject$.asObservable();
  formColCount: number = 2;

  formItemsBehaviourSubject$ = new BehaviorSubject<any[]>([]);
  formItems$ = this.formItemsBehaviourSubject$.asObservable();
  formItems: any[] = [];

  constructor(
    taskDataService: TaskDataService,
    private readonly messageService: MessageService,
    private readonly loggingService: LoggingService
  ) {
    super(taskDataService);
    // this.onFileUpload = this.onFileUpload.bind(this);

    this.onRequestTypeSelectionChanged =
      this.onRequestTypeSelectionChanged.bind(this);
    this.formItems$.subscribe((items: any[]) => {
      this.formItems = items;
    });
    this.transactiontypes = [
      { 'id': 'a', 'name': 'Funds to MSUS' },
      { 'id': 'b', 'name': 'Non-Cash Transaction' }
    ];

    // Subscribe to form items changes
    this.formItems$.subscribe((items: any[]) => {
      this.formItems = items;
    });

    this.formColCount$.subscribe((colcount: number) => {
      this.formColCount = colcount;
    });

    // Subscribe to loan data changes
    this.loanNumbers$.subscribe((data) => {
      this.loanNumbers = data;
    });
  }

  onRequestTypeSelectionChanged(e: any) {
    // Safety check for selectedItem
    if (!e.selectedItem || !e.selectedItem.name) {
      console.warn('Invalid selection event:', e);
      return;
    }

    // Reset loan numbers if changing request type
    if (this.currentRequestType && this.currentRequestType !== e.selectedItem.name) {
      if (this.payoffPaydownData && this.payoffPaydownData.loanNumbers) {
        this.payoffPaydownData.loanNumbers = [];
      }
      this.isGridInEditMode = false;
      this.shouldDisableCheckbox = false;
    }

    this.currentRequestType = e.selectedItem.name;
    let config = [
      {
        dataField: "requestType",
        editorType: "dxSelectBox",
        label: { text: "Request Type" },
        colSpan: 2,
        cssClass: "dx-widget",
        editorOptions: {
          items: this.requestTypes,
          valueExpr: "name",
          displayExpr: "name",
          required: true,
          searchMode: "contains",
          searchEnabled: true,
        },
        validationRules: [
          { type: "required", message: "Request Type is required" },
        ],
      },
      {
        itemType: "simple",
        name: "loanAmountsGridWithCheckbox",
        colSpan: 2,
        label: { visible: false },
        template: "loanAmountsGridTemplate",
      },
      {
        // Proxy validator for loan numbers grid
        dataField: "loanAmountsProxy",
        colSpan: 2,
        itemType: "simple",
        label: { visible: false },
        editorType: "dxTextBox",
        editorOptions: {
          visible: false,
          value: this.payoffPaydownData.loanNumbers
            ? this.payoffPaydownData.loanNumbers.length.toString()
            : "0",
        },
        validationRules: [
          {
            type: "custom",
            message: "At least one loan number must be entered",
            validationCallback: (e) => {
              const hasData =
                Array.isArray(this.payoffPaydownData.loanNumbers) &&
                this.payoffPaydownData.loanNumbers.length > 0;
              return hasData;
            },
            reevaluate: true,
          },
        ],
      },
      {
        dataField: "transactionType",
        editorType: "dxSelectBox",
        label: { text: "Transaction Type" },
        colSpan: 2,
        cssClass: "dx-widget",
        editorOptions: {
          items: this.transactiontypes,
          valueExpr: "name",
          displayExpr: "name",
          required: true,
        },
        validationRules: [
          { type: "required", message: "Transaction Type is required" },
        ],
      },
      {
        dataField: "triggerType",
        editorType: "dxSelectBox",
        label: { text: "Trigger Type" },
        colSpan: 2,
        cssClass: "dx-widget",
        editorOptions: {
          items: this.triggertypes,
          valueExpr: "name",
          displayExpr: "name",
          required: true,
        },
        validationRules: [
          { type: "required", message: "Trigger Type is required" },
        ],
      },
      {
        dataField: "expectedPayoffDate",
        editorType: "dxDateBox",
        label: { text: "Expected Payoff Date" },
        colSpan: 2,
        cssClass: "dx-widget",
        editorOptions: {
          value: this.expectedPayoffDate,
          width: "100%",
        },
        validationRules: [
          { type: "required", message: "Expected Payoff Date is required" },
        ],
      },
      {
        dataField: "nextTaxDueDate",
        editorType: "dxDateBox",
        visible: false,
        label: { text: "Next Tax Due Date" },
        colSpan: 1,
        cssClass: "dx-widget",
        editorOptions: {
          value: this.nextTaxDueDate,
          width: "100%",
        },
      },
      {
        itemType: "group",
        colCount: 3,
        colSpan: 2,
        items: [
          {
            dataField: "isUrgent",
            editorType: "dxCheckBox",
            label: { text: "Rush Job?" },
            colSpan: 1,
            cssClass: "dx-widget",
            editorOptions: {
              text: "",
              onValueChanged: (e: any) => {
                this.isUrgent = e.value;
              },
            },
          },
          {
            dataField: "isRushFee",
            editorType: "dxCheckBox",
            label: { text: "Rush Fee?" },
            colSpan: 1,
            cssClass: "dx-widget",
            editorOptions: {
              text: "",
              onValueChanged: (e: any) => {
                this.isRushFee = e.value;
              },
            },
          },
          {
            dataField: "reservesOnLoan",
            editorType: "dxCheckBox",
            label: { text: "Reserves on Loan?" },
            colSpan: 1,
            cssClass: "dx-widget",
            editorOptions: {
              text: "",
              onValueChanged: (e: any) => {
                this.reservesOnLoan = e.value;
              },
            },
          },
        ],
      },
      {
        dataField: "description",
        editorType: "dxTextArea",
        label: { text: "Description " },
        colSpan: 2,
        cssClass: "dx-widget",
        editorOptions: {
          required: "true",
          defaultValue: "",
          height: 100,
        },
        validationRules: [
          { type: "required", message: "Description is required" },
        ],
      },
      {
        colSpan: 2,
        horizontalAlignment: "center",
        itemType: "simple",
        name: "fileUpload",
        label: { text: "Upload Files *" },
        template: "fileUploadTemplate",
      },
      {
        // work around to bind file upload component to form and validate it
        dataField: "fileUploadProxy",
        colSpan: 2,
        itemType: "simple",
        label: { visible: false },
        editorType: "dxTextBox",
        editorOptions: {
          visible: false,
          value: this.files.length.toString(),
        },
        validationRules: [
          {
            type: "custom",
            message: "At least one file is required",
            validationCallback: (e) => this.onValidateFileAttachments(e),
            reevaluate: true,
          },
        ],
      },
      {
        colSpan: 2,
        horizontalAlignment: "center",
        cssClass: "borrow-submit",
        editorType: "dxButton",
        editorOptions: { text: "Submit", useSubmitBehavior: true },
      },
    ];

    this.formItemsBehaviourSubject$.next(config);
  }

  onFileUpload(file: File){
    this.files.push(file);
    this.updateFileUploadValidation();
    if(this.isEditable)
    {
      this.onFileUploadEventEmitter.emit(file);
    }
    else{
      this.onFileUploadEventEmitter.emit(null);
    }
  }

  onFileRemove(event: any) {
    const file = event?.file || event;
    this.files = this.files.filter(f => f !== file);
    this.updateFileUploadValidation();
    this.onFileRemoveEventEmitter.emit(file);
  }

  updateFileUploadValidation(): void {
    if (this.currentFormInstance) {
      const fileUploadProxyEditor = this.currentFormInstance.getEditor("fileUploadProxy");
      if (fileUploadProxyEditor) {
        fileUploadProxyEditor.option("value", this.files.length.toString());
      }
    }
  }

  onFormContentReady(e: any): void {
    this.currentFormInstance = e.component;
  }

  onFieldDataChanged(e: any): void {
    // Handle field changes if needed
  }

  private normalizeLoanNumbersForGrid(): void {
    if (!this.payoffPaydownData) {
      return;
    }

    if (!Array.isArray(this.payoffPaydownData.loanNumbers)) {
      this.payoffPaydownData.loanNumbers = [];
      return;
    }

    if (
      this.payoffPaydownData.loanNumbers.length > 0 &&
      typeof this.payoffPaydownData.loanNumbers[0] === 'string'
    ) {
      this.payoffPaydownData.loanNumbers = (this.payoffPaydownData.loanNumbers as unknown as string[]).map((loanNumber) => ({
        key: this.generateUniqueId(),
        loanNumber,
      }));
    }
  }

  // Generate unique ID for grid rows
  private generateUniqueId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  // Get loan data for the grid (filters based on existing selections)
  getLoanData() {
    if (this.payoffPaydownData.loanNumbers && this.payoffPaydownData.loanNumbers.length > 0) {
      // Search in allLoanData to find loan info regardless of active/inactive status
      const firstLoan = this.allLoanData.find((ld) => ld.name === this.payoffPaydownData.loanNumbers[0].loanNumber);

      if (!firstLoan) {
        return this.loanNumbers;
      }

      // Extract deal identifier from loan name (first 6 digits of the loan number)
      const extractDealId = (loanName: string): string | null => {
        if (!loanName) return null;
        const match = loanName.match(/^(\d{6})/);
        return match ? match[1] : null;
      };

      const firstLoanDealId = extractDealId(firstLoan.name);

      if (!firstLoanDealId) {
        return this.loanNumbers;
      }

      const existingLoanNumbers = this.payoffPaydownData.loanNumbers.map((item) => item.loanNumber);

      // Filter loans that have the same deal ID (same first 6 digits)
      return this.allLoanData.filter((loan) => {
        const loanDealId = extractDealId(loan.name);
        return loanDealId === firstLoanDealId && !existingLoanNumbers.includes(loan.name);
      });
    }

    return this.loanNumbers;
  }

  // Update checkbox disabled state based on grid state
  private updateCheckboxDisabledState(): void {
    const hasItems = this.payoffPaydownData.loanNumbers && this.payoffPaydownData.loanNumbers.length > 0;
    this.shouldDisableCheckbox = this.isGridInEditMode || hasItems;
  }

  override onShowInactiveLoansChanged(e: any): void {
    this.showInactiveLoans = e.value;
    this.isUpdatingLoanData = true;
    super.onShowInactiveLoansChanged(e);

    // Reset flag and refresh grid
    setTimeout(() => {
      this.isUpdatingLoanData = false;
    }, 50);
  }

  onGridShowInactiveLoansChange(checked: boolean): void {
    this.showInactiveLoans = checked;
    this.updateCheckboxDisabledState();
  }

  onGridEditModeChange(inEditMode: boolean): void {
    this.isGridInEditMode = inEditMode;
    this.updateCheckboxDisabledState();
  }

  onGridLoanAmountsChange(loanAmounts: any[]): void {
    this.payoffPaydownData.loanNumbers = loanAmounts;
    this.updateCheckboxDisabledState();
  }

  onValidateFileAttachments(e: any): boolean {
    if (this.files.length === 0) {
      e.isValid = false;
      return false;
    }
    e.isValid = true;
    return true;
  }

  async onSubmit(e: Event) {
    e.preventDefault();

    let validationResult = validationEngine.validateGroup("formGroup");

    if (validationResult.isValid) {
      try {
        let request: ITaskRequest = new TaskRequest();
        let requestType = this.requestTypes.find((t) => t.name == this.payoffPaydownData.requestType);
        request.templateId = requestType.id;

        request.name = requestType.name;
        // Use the first loan number from the array as the reference name
        request.referenceName = this.payoffPaydownData.loanNumbers && this.payoffPaydownData.loanNumbers.length > 0
          ? this.payoffPaydownData.loanNumbers[0].loanNumber
          : '';
        request.referenceType = requestType.referenceType;

        // Ensure nextTaxDueDate is explicitly included in the serialized data
        const requestJsonData = {
          ...this.payoffPaydownData,
          nextTaxDueDate: this.payoffPaydownData.nextTaxDueDate ?? null
        };

        let formData: FormData = new FormData();
        formData.append('templateId', request.templateId);
        formData.append('name', request.name);
        formData.append('referenceName', request.referenceName);
        formData.append('requestJsonData', this.removeProxyData(JSON.stringify(requestJsonData)));
        formData.append('referenceType', request.referenceType);
        formData.append('isUrgent', request.isUrgent ? 'true' : 'false');

        if (this.isEditable) {
          formData.append('parentId', this.task.parentId);
        }

        this.files.forEach((f) => { formData.append('files', f, f.name) });
        this.isLoading = true;

        const response = await lastValueFrom(this.taskDataService.insertTaskAsFormData(formData));
        sendNotificationMessage(`${response[1].data[0].task.taskId} task created`, this.messageService);

        this.isLoading = false;

        this.formElement.nativeElement.reset();
        this.formElement.nativeElement.submit();
      } catch (err: any) {
        this.isLoading = false;
        this.loggingService.logError(err, SeverityLevel.Error);
        notify(err.message, 'error', getNotificationTimeout());
      }
    }
  }

  getTriggerTypes() {
    return [
      { 'id': 'b', 'name': 'Borrower' },
      { 'id': 'c', 'name': 'Client' },
      { 'id': 'i', 'name': 'Internal' }
    ];
  }

  override async ngOnInit() {
    try {
      this.isLoading = true;
      const requestTypeData = await lastValueFrom(this.taskDataService.getTaskTemplatesByType(this.taskTemplateName));
      this.requestTypes = requestTypeData[1];

      await super.ngOnInit();
      this.triggertypes = this.getTriggerTypes();

      this.isEditable = this.task != null ? true : false;

      if (this.isEditable) {
        this.payoffPaydownData = FormUtils.mapFromTask<PayoffPayDownRequestData>(this.task, PayoffPayDownRequestData);
      } else {
        this.payoffPaydownData = new PayoffPayDownRequestData();
      }

      this.normalizeLoanNumbersForGrid();

      // Build the form configuration
      this.isLoading = false;
    } catch (err: any) {
      this.loggingService.logError(err, SeverityLevel.Error);
      this.isLoading = false;
      notify(err.message, 'error', getNotificationTimeout());
    }
  }

    removeProxyData(data: string): string {
    if(data) {
      let json = JSON.parse(data);
      delete json["fileUploadProxy"];
      delete json["loanAmountsProxy"];
      return JSON.stringify(json);
    }
    return "{}";
  }
}


export class PayoffPaydownModule { }
